import { CircleInfoIcon } from "@xola/icons";
import { Alert, Checkbox, Modal, SubmitButton, Table, Tooltip } from "@xola/ui-kit";
import { DemographicIcon, FormField, InputField, now, requiredString } from "common";
import { produce } from "immer";
import { omit } from "lodash";
import React, { ChangeEvent, useState } from "react";
import { Except } from "type-fest";
import {
    CheckInRule,
    CheckInRuleResponse,
    CheckInRuleResponseStatus,
    CheckInVerificationStatus,
    ExperiencePurchaseItem,
    FormGuest,
    Guest,
    GuestCheckInAction,
    GuestStatus,
    Unit,
} from "types";
import { array, bool, lazy, object, string } from "yup";
import { useSeller, useUser } from "../../../../hooks/useAccount";
import { useResource } from "../../../../hooks/useResource";
import { isDefaultDemographic } from "../../../../models/DemographicUnit";
import { Form } from "../../../Common/Form";
import { NoTranslate } from "../../../Common/NoTranslate";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { XolaFormik } from "../../../Common/XolaFormik";
import { AutoTooltipContainer } from "@/components/Common/AutoTooltipContainer";
import {
    useCheckIfMemberVerificationRequired,
    useMemberVerificationModal,
} from "@/components/PurchaseModification/CheckInPurchaseGuests/useMemberVerificationModal";

interface CheckInAndPrintTicketModalProps {
    guests: Guest[];
    isSaving: boolean;
    /** If check-in modal is opened for a specified purchase item */
    purchaseItem?: ExperiencePurchaseItem;
    onPrint: (values: any) => void;
    onClose: () => void;
    isOpen: boolean;
}

export const CheckInAndPrintTicketModal = ({ isOpen, onClose, ...rest }: CheckInAndPrintTicketModalProps) => {
    return (
        <Modal isOpen={isOpen} size="huge" onClose={onClose}>
            <ModalContent onClose={onClose} {...rest} />
        </Modal>
    );
};

interface CheckInRulesSelectionObject {
    [k: string]: boolean;
}

const getValidationSchema = () => {
    return lazy((values) => {
        return object().shape({
            guests: array().of(
                object().shape({
                    ticketPrintStatus: object().shape({
                        printed: bool(),
                        checkInRuleResponses: array().when("printed", {
                            is: true,
                            then: array().of(
                                object().shape({
                                    required: bool(),
                                    status: string().when("required", {
                                        is: true,
                                        then: string().oneOf([CheckInRuleResponseStatus.PASS]),
                                    }),
                                }),
                            ),
                        }),
                    }),
                }),
            ),
            reason: getShouldShowReason(values.guests) ? requiredString() : string().notRequired(),
        });
    });
};

const getCheckInRulesDefaultResponse = (rules?: CheckInRule[]) => {
    return rules?.map((rule) => ({ ...rule, status: CheckInRuleResponseStatus.FAIL }));
};

const getDefaultPrintStatus = (autoPrint: boolean, rules: CheckInRule[]) => {
    return {
        printed: autoPrint,
        checkInRuleResponses: getCheckInRulesDefaultResponse(rules),
        reason: "",
    };
};

const getInitialValues = (guests: Guest[], autoCheckIn: boolean, autoPrint: boolean, checkInRules: CheckInRule[]) => {
    return {
        guests: guests.map((guest) => {
            const updatedGuest: FormGuest = {
                ...guest,
                guestStatus: autoCheckIn ? GuestStatus.ARRIVED : guest.guestStatus,
                ticketPrintStatus: guest.ticketPrintStatus ?? getDefaultPrintStatus(autoPrint, checkInRules ?? []),
            };

            // remove attributes unrelated to check-in and printing from guest object,
            // and read-only fields that must never be echoed back to the write API
            return omit(updatedGuest, ["waiver", "participant", "ticketCode"]);
        }) as FormGuest[],
    };
};

const updateCheckInRuleStatus = (
    checkInRuleResponses: CheckInRuleResponse[],
    rule: CheckInRule | null = null,
    isChecked = false,
) => {
    const status = isChecked ? CheckInRuleResponseStatus.PASS : CheckInRuleResponseStatus.FAIL;
    if (!rule) {
        return checkInRuleResponses.map((r) => {
            return { ...r, status };
        });
    }

    return checkInRuleResponses.map((r) => {
        return r.id === rule.id ? { ...r, status } : r;
    });
};

const getResponseById = (responseId: ID, responses: CheckInRuleResponse[]) => {
    return responses.find((response) => response.id === responseId);
};

const getShouldShowReason = (guests: Guest[]) => {
    return guests.some((guest) => {
        const { printCount, printed } = guest?.ticketPrintStatus ?? {};
        return printed && (printCount ?? 0) > 0;
    });
};

const ModalContent = ({
    guests,
    purchaseItem,
    isSaving,
    onClose,
    onPrint,
}: Except<CheckInAndPrintTicketModalProps, "isOpen">) => {
    const user = useUser();
    const seller = useSeller();
    const { autoCheckIn = true, autoPrint = true } = user.preferences?.ticket ?? {};
    const { checkInRules = [] } = seller.preferences?.ticket ?? {};
    const [checkInAll, setCheckInAll] = useState(autoCheckIn);
    const [printAll, setPrintAll] = useState(autoPrint);
    const [checkInRulesSelection, setCheckInRulesSelection] = useState<CheckInRulesSelectionObject>({});
    const { isMemberVerificationRequired } = useCheckIfMemberVerificationRequired(purchaseItem);
    const { verifyMember, MemberVerificationModal } = useMemberVerificationModal();

    const isItemLevelEquipment = guests.length > 0 && guests.every((guest) => !guest.unit?.id);

    const initialValues: { guests: FormGuest[]; reason?: string } = getInitialValues(
        guests,
        autoCheckIn,
        autoPrint,
        checkInRules,
    );

    const handleSubmit = async (values: typeof initialValues) => {
        let verificationStatus: CheckInVerificationStatus | undefined;
        const isAnyGuestCheckingIn = values.guests.some(
            (guest) => !guest.checkInCount && guest.guestStatus === GuestStatus.ARRIVED,
        );
        if (isAnyGuestCheckingIn && isMemberVerificationRequired()) {
            verificationStatus = await verifyMember();
            if (!verificationStatus) {
                onClose();
                return;
            }
        }

        const payload = {
            verificationStatus: verificationStatus,
            guests: produce(values.guests, (guestsDraft) => {
                let isPrimaryGuestUpdated = false;
                for (const guest of guestsDraft) {
                    if (!guest.checkInCount && guest.guestStatus === GuestStatus.ARRIVED) {
                        /**
                         * As of now, we only support SINGLE check-in from check-in and print modal, so no need to add `incrementCheckInCount` again in the payload if guest is already checked-in again
                         *
                         * The `!guest.checkInCount` is added for the following scenario:
                         * Suppose, we have 5 guests for a scheduled experience purchase (SINGLE check-in). and from a dashboard roster, seller checked in first guest from the list;
                         * and the next time seller clicks on "Check-in all guests". Now, the first guest is already checked in. So, if we add `incrementCheckInCount` again for that guest, the API would throw error "Guest #1 already checked-in 1 times"
                         * We don't want that. This time, we just need to check in the rest of the guests.
                         **/
                        if (guest.primary) {
                            isPrimaryGuestUpdated = true;
                        }

                        guest.incrementCheckInCount = GuestCheckInAction.CHECK_IN;
                        guest.checkIns = [
                            {
                                createdAt: now(),
                                verificationStatus: guest.primary ? verificationStatus : undefined,
                            },
                        ];
                    }

                    delete guest.checkInCount;

                    if (getShouldShowReason([guest])) {
                        guest.ticketPrintStatus = {
                            ...guest.ticketPrintStatus,
                            reason: values.reason,
                        };
                    }

                    if (!guest.ticketPrintStatus?.printed) {
                        delete guest.ticketPrintStatus;
                    }
                }

                if (verificationStatus && !isPrimaryGuestUpdated) {
                    for (const guest of guestsDraft) {
                        if (guest.incrementCheckInCount === GuestCheckInAction.CHECK_IN && guest.checkIns?.length) {
                            guest.checkIns[0].verificationStatus = verificationStatus;
                        }
                    }
                }
            }),
        };

        return onPrint(payload);
    };

    return (
        <>
            <XolaFormik initialValues={initialValues} validationSchema={getValidationSchema} onSubmit={handleSubmit}>
                {({ values, handleSubmit, setFieldValue, errors, isSubmitting }) => {
                    const handleCheckInAll = (event: ChangeEvent<HTMLInputElement>) => {
                        const { checked: isChecked } = event.target;
                        setCheckInAll(isChecked);
                        const guests = values.guests.map((guest) => {
                            return { ...guest, guestStatus: isChecked ? GuestStatus.ARRIVED : GuestStatus.PENDING };
                        });
                        setFieldValue("guests", guests);
                    };

                    const handlePrintAll = (event: ChangeEvent<HTMLInputElement>) => {
                        const { checked: isChecked } = event.target;
                        setPrintAll(isChecked);
                        const guests = values.guests.map((guest) => {
                            return {
                                ...guest,
                                ticketPrintStatus: {
                                    ...guest.ticketPrintStatus,
                                    printed: isChecked,
                                    checkInRuleResponses: isChecked
                                        ? guest?.ticketPrintStatus?.checkInRuleResponses
                                        : updateCheckInRuleStatus(guest?.ticketPrintStatus?.checkInRuleResponses ?? []),
                                },
                            };
                        });
                        setFieldValue("guests", guests);
                    };

                    const handleAllCheckInRuleSelection = (rule: CheckInRule, event: ChangeEvent<HTMLInputElement>) => {
                        const { checked: isChecked } = event.target;
                        checkInRulesSelection[rule.name] = isChecked;
                        setCheckInRulesSelection(checkInRulesSelection);
                        const guests = values.guests.map((guest) => {
                            return {
                                ...guest,
                                ticketPrintStatus: {
                                    ...guest.ticketPrintStatus,
                                    checkInRuleResponses: updateCheckInRuleStatus(
                                        guest.ticketPrintStatus?.checkInRuleResponses ?? [],
                                        rule,
                                        guest.ticketPrintStatus?.printed ? isChecked : false,
                                    ),
                                },
                            };
                        });
                        setFieldValue("guests", guests);
                    };

                    const handleGuestStatusChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
                        const { checked: isChecked } = event.target;
                        const { guests } = values;
                        guests[index].guestStatus = isChecked ? GuestStatus.ARRIVED : GuestStatus.PENDING;

                        setCheckInAll(guests.every((guest) => guest.guestStatus === GuestStatus.ARRIVED));
                        setFieldValue("guests", guests);
                    };

                    const handleGuestPrintStatusChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
                        const { checked: shouldPrint } = event.target;
                        const { guests } = values;
                        guests[index] = {
                            ...guests[index],
                            ticketPrintStatus: {
                                ...guests[index].ticketPrintStatus,
                                printed: shouldPrint,
                                checkInRuleResponses: shouldPrint
                                    ? guests[index]?.ticketPrintStatus?.checkInRuleResponses
                                    : updateCheckInRuleStatus(
                                          guests[index]?.ticketPrintStatus?.checkInRuleResponses ?? [],
                                      ),
                            },
                        };
                        setPrintAll(guests.every((guest) => guest.ticketPrintStatus?.printed));
                        setFieldValue("guests", guests);
                    };

                    const handleGuestCheckInRuleSelection = (
                        index: number,
                        event: ChangeEvent<HTMLInputElement>,
                        rule: CheckInRule,
                    ) => {
                        const { checked: isChecked } = event.target;
                        const { guests } = values;
                        guests[index] = {
                            ...guests[index],
                            ticketPrintStatus: {
                                ...guests[index].ticketPrintStatus,
                                printed: guests[index].ticketPrintStatus?.printed ?? false,
                                checkInRuleResponses: updateCheckInRuleStatus(
                                    guests[index]?.ticketPrintStatus?.checkInRuleResponses ?? [],
                                    rule,
                                    isChecked,
                                ),
                            },
                        };

                        checkInRulesSelection[rule.name] = guests.every((guest) => {
                            const response = guest.ticketPrintStatus?.checkInRuleResponses?.find((response) => {
                                return response.id === rule.id;
                            });
                            return response?.status === CheckInRuleResponseStatus.PASS;
                        });

                        setCheckInRulesSelection(checkInRulesSelection);
                        setFieldValue("guests", guests);
                    };

                    const isCheckInError = (index: number) => {
                        if (Array.isArray(errors.guests)) {
                            return errors.guests?.some((guest: any) => {
                                return guest?.ticketPrintStatus?.checkInRuleResponses[index]?.status;
                            });
                        }
                    };

                    const anyCheckInRuleError = () => {
                        if (Array.isArray(errors.guests)) {
                            return errors.guests?.some((guest: any) => {
                                return guest?.ticketPrintStatus?.checkInRuleResponses.length > 0;
                            });
                        }
                    };

                    const checkInResponsePassedForGuest = (guestIndex: number, checkInRuleIndex: number) => {
                        const guest: any = errors.guests?.[guestIndex];
                        return guest?.ticketPrintStatus?.checkInRuleResponses?.[checkInRuleIndex]?.status;
                    };

                    const isRuleCheckedByAllGuests = (ruleId: ID) => {
                        const guestsToPrint = values.guests.filter((guest) => guest.ticketPrintStatus?.printed);
                        return (
                            guestsToPrint.length > 0 &&
                            guestsToPrint.every((guest) => {
                                const rule = guest.ticketPrintStatus?.checkInRuleResponses?.find((response) => {
                                    return response.id === ruleId;
                                });
                                return rule?.status === CheckInRuleResponseStatus.PASS;
                            })
                        );
                    };

                    const shouldShowReason = getShouldShowReason(values.guests);

                    const isCheckInSelected = values.guests.some((guest) => guest.guestStatus === GuestStatus.ARRIVED);
                    const isPrintSelected = values.guests.some((guest) => guest.ticketPrintStatus?.printed);
                    const submitButtonCopy =
                        isCheckInSelected && isPrintSelected
                            ? "Check-In & Print"
                            : !isCheckInSelected && isPrintSelected
                            ? "Print"
                            : "Check-In";

                    const shouldShowPrintedCount = values.guests.some((guest) => guest.ticketPrintStatus?.printCount);
                    return (
                        <Form>
                            <Modal.Header>Check-In and Print Tickets</Modal.Header>

                            <Modal.Body>
                                {anyCheckInRuleError() ? (
                                    <Alert color="danger" className="mb-8">
                                        <div className="flex items-center space-x-4 p-2">
                                            <CircleInfoIcon size="large" />
                                            <div className="flex flex-col space-y-1">
                                                <span className="font-bold">Error</span>
                                                <span>Selected guests are required to check mandatory rules</span>
                                            </div>
                                        </div>
                                    </Alert>
                                ) : null}

                                <Table>
                                    <Table.Head>
                                        <Table.Row>
                                            {!isItemLevelEquipment && (
                                                <Table.Header className="align-bottom !text-sm">Type</Table.Header>
                                            )}
                                            <Table.Header className="min-w-[100px] text-center align-bottom !text-sm">
                                                <div className="flex flex-col items-center space-y-2">
                                                    <span>Check-in</span>
                                                    {values.guests.length > 1 ? (
                                                        <Checkbox checked={checkInAll} onChange={handleCheckInAll} />
                                                    ) : null}
                                                </div>
                                            </Table.Header>
                                            {seller.preferences.ticket?.enabled && (
                                                <Table.Header className="w-[100px] text-center align-bottom !text-sm">
                                                    <div className="flex flex-col items-center space-y-2">
                                                        <span>Print</span>
                                                        {values.guests.length > 1 ? (
                                                            <Checkbox checked={printAll} onChange={handlePrintAll} />
                                                        ) : null}
                                                    </div>
                                                </Table.Header>
                                            )}

                                            {checkInRules?.map((checkInRule, index) => (
                                                <Table.Header
                                                    key={checkInRule.id}
                                                    className="w-[100px] text-center align-bottom !text-sm"
                                                >
                                                    <div className="flex flex-col items-center space-y-2">
                                                        <div className="flex space-x-1">
                                                            <div className="w-[65px]">
                                                                <NoTranslate>
                                                                    <AutoTooltipContainer
                                                                        tooltipContent={checkInRule.name}
                                                                    >
                                                                        {checkInRule.name}
                                                                    </AutoTooltipContainer>
                                                                </NoTranslate>
                                                            </div>
                                                            {checkInRule.required ? (
                                                                <Tooltip
                                                                    content="All guests are required to check this rule"
                                                                    placement="top"
                                                                >
                                                                    <CircleInfoIcon color="text-danger" />
                                                                </Tooltip>
                                                            ) : null}
                                                        </div>
                                                        {values.guests.length > 1 ? (
                                                            <Checkbox
                                                                disabled={!isPrintSelected}
                                                                checked={isRuleCheckedByAllGuests(checkInRule.id)}
                                                                classNames={{
                                                                    checkbox: isCheckInError(index) && "!border-danger",
                                                                }}
                                                                onChange={(event_: ChangeEvent<HTMLInputElement>) =>
                                                                    handleAllCheckInRuleSelection(checkInRule, event_)
                                                                }
                                                            />
                                                        ) : null}
                                                    </div>
                                                </Table.Header>
                                            ))}

                                            <Table.Header className="text-center align-bottom !text-sm">
                                                Name
                                            </Table.Header>

                                            {shouldShowPrintedCount && (
                                                <Table.Header className="text-center align-bottom !text-sm">
                                                    {" "}
                                                </Table.Header>
                                            )}
                                        </Table.Row>
                                    </Table.Head>

                                    {values.guests.length > 0 ? (
                                        <Table.Body>
                                            {values.guests.map((guest, index) => (
                                                <Table.Row key={guest.id} className="!border-none">
                                                    {!isItemLevelEquipment && (
                                                        <Table.Cell className="py-4">
                                                            <DemographicItem unitId={guest.unit.id} />
                                                        </Table.Cell>
                                                    )}

                                                    <Table.Cell className=" py-4 text-left">
                                                        <div className="flex justify-center">
                                                            <Checkbox
                                                                label=""
                                                                checked={guest.guestStatus === GuestStatus.ARRIVED}
                                                                onChange={(event_: ChangeEvent<HTMLInputElement>) => {
                                                                    handleGuestStatusChange(index, event_);
                                                                }}
                                                            />
                                                        </div>
                                                    </Table.Cell>

                                                    {seller.preferences.ticket?.enabled && (
                                                        <Table.Cell className="flex justify-center py-4 text-left">
                                                            <div className="flex justify-center">
                                                                <Checkbox
                                                                    checked={guest.ticketPrintStatus?.printed ?? false}
                                                                    onChange={(
                                                                        event_: ChangeEvent<HTMLInputElement>,
                                                                    ) => {
                                                                        handleGuestPrintStatusChange(index, event_);
                                                                    }}
                                                                />
                                                            </div>
                                                        </Table.Cell>
                                                    )}

                                                    {checkInRules?.map((checkInRule, checkInRuleIndex) => (
                                                        <Table.Cell key={checkInRule.id} className="py-4 text-left">
                                                            <div className="flex justify-center">
                                                                <Checkbox
                                                                    disabled={isCheckInCheckBoxDisabled(guest)}
                                                                    checked={isCheckboxChecked(guest, checkInRule)}
                                                                    classNames={{
                                                                        checkbox:
                                                                            checkInResponsePassedForGuest(
                                                                                index,
                                                                                checkInRuleIndex,
                                                                            ) && "!border-danger",
                                                                    }}
                                                                    onChange={(
                                                                        event_: ChangeEvent<HTMLInputElement>,
                                                                    ) => {
                                                                        handleGuestCheckInRuleSelection(
                                                                            index,
                                                                            event_,
                                                                            checkInRule,
                                                                        );
                                                                    }}
                                                                />
                                                            </div>
                                                        </Table.Cell>
                                                    ))}

                                                    <Table.Cell className="no-translate py-4 text-center">
                                                        {guest.name}
                                                    </Table.Cell>

                                                    {guest.ticketPrintStatus?.printCount && (
                                                        <Table.Cell className="no-translate py-4 text-center">
                                                            {guest.ticketPrintStatus?.printCount} printed
                                                        </Table.Cell>
                                                    )}
                                                </Table.Row>
                                            ))}
                                        </Table.Body>
                                    ) : null}
                                </Table>

                                {shouldShowReason && (
                                    <FormField label="Reason for re-printing?" name="reason" className="mt-4">
                                        <InputField isRequired placeholder="Reason" />
                                    </FormField>
                                )}
                            </Modal.Body>

                            <Modal.Footer>
                                <fieldset className="flex justify-end space-x-4">
                                    <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                                    <SubmitButton isLoading={isSaving || isSubmitting} onClick={handleSubmit}>
                                        {submitButtonCopy}
                                    </SubmitButton>
                                </fieldset>
                            </Modal.Footer>
                        </Form>
                    );
                }}
            </XolaFormik>
            <MemberVerificationModal purchaseItem={purchaseItem} />
        </>
    );
};

const isCheckInCheckBoxDisabled = (guest: Guest) => {
    const printStatus = guest.ticketPrintStatus;
    if (printStatus && !printStatus?.printed) {
        return true;
    }

    return printStatus?.printCount && printStatus?.printCount > 0 && !printStatus.printed;
};

const isCheckboxChecked = (guest: Guest, checkInRule: CheckInRule) => {
    const resp = getResponseById(checkInRule.id, guest.ticketPrintStatus?.checkInRuleResponses ?? []);
    return resp?.status === CheckInRuleResponseStatus.PASS;
};

const DemographicItem = ({ unitId }: { unitId: ID }) => {
    const { data: demographicUnit } = useResource<Unit>(["units", unitId]);

    return demographicUnit ? (
        <div className="flex items-center space-x-1">
            <DemographicIcon name={demographicUnit.name} />
            <NoTranslate if={!isDefaultDemographic(demographicUnit.name)}>{demographicUnit.name}</NoTranslate>
        </div>
    ) : null;
};
